// PrivacyPage — UAVSimNet Privacy Notice (UK GDPR / DPA 2018 compliant).
// Issued by The RLK Group (Scottish limited company, SC781391).
// Lawful-basis attribution uses Article 6 UK GDPR citations.

import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import { buildBreadcrumbListSchema } from "@/lib/schema";

const EFFECTIVE_DATE = "25 April 2026";

export default function PrivacyPage() {
    return (
        <>
            <SeoHead
                path="/privacy"
                schemaJsonLd={[
                    buildBreadcrumbListSchema({ path: "/privacy", label: "Privacy Notice" }),
                ]}
            />
        <div data-testid="privacy-page">
            <section className="section" style={{ paddingTop: 64 }}>
                <div className="section__inner legal-doc">
                    <span className="section__eyebrow">// LEGAL · PRIVACY NOTICE</span>
                    <h1 className="section__heading">Privacy Notice</h1>
                    <div
                        className="legal-doc__banner"
                        data-testid="privacy-issued-banner"
                    >
                        Issued by The RLK Group · Company No. SC781391 · Effective from {EFFECTIVE_DATE}
                    </div>

                    <h2>1. Who we are</h2>
                    <p>
                        This notice is issued by <strong>The RLK Group</strong>, a
                        private limited company incorporated in Scotland under
                        company number <strong>SC781391</strong>, with its registered
                        office at Unit 4 Strathclyde Business Centre, Cambuslang,
                        United Kingdom, G72 7XR ("<strong>RLK</strong>", "
                        <strong>we</strong>", "<strong>us</strong>"). We are the
                        data controller for personal data processed in connection
                        with our counter-UAS mission analysis platform,{" "}
                        <strong>UAVSimNet</strong> (the "<strong>Platform</strong>"),
                        and the public website that describes it (the "
                        <strong>Site</strong>").
                    </p>
                    <p>
                        For privacy enquiries, to exercise your rights under UK
                        GDPR, or to contact our data protection function, write to
                        The RLK Group in writing at its registered office: Unit 4
                        Strathclyde Business Centre, Cambuslang, United Kingdom,
                        G72 7XR. We will respond within the time limits set out in
                        §10.
                    </p>

                    <h2>2. Scope</h2>
                    <p>
                        This notice applies to personal data we process about three
                        categories of individual: (a) people who submit an access
                        request through the public contact form on the Site;
                        (b) Authorised Users of the Platform — individuals who hold
                        credentials issued by us to access UAVSimNet on behalf of a
                        customer organisation; and (c) general visitors to the Site.
                        It supplements, and should be read alongside, our Terms of
                        Service (<Link to="/terms">/terms</Link>) and Acceptable Use
                        Policy (<Link to="/aup">/aup</Link>).
                    </p>

                    <h2>3. Personal data we collect</h2>

                    <h3>3.1 From access-request submissions</h3>
                    <ul>
                        <li>Full name and organisation name.</li>
                        <li>Work email address.</li>
                        <li>Country of operation (ISO-3166 alpha-2 code).</li>
                        <li>Role or job title.</li>
                        <li>
                            Free-form description of the intended use case and the
                            problem you are trying to solve.
                        </li>
                        <li>
                            The four compliance and consent attestations captured at
                            submission, each with its own timestamp: compliance
                            attestation, Terms acceptance, AUP acceptance, and
                            training-mirror participation consent.
                        </li>
                    </ul>

                    <h3>3.2 From Authorised User accounts</h3>
                    <ul>
                        <li>
                            Authentication data — username (derived from the email
                            local-part), password stored as a salted hash, session
                            tokens, session issue and expiry timestamps.
                        </li>
                        <li>
                            Authentication events — login success, login failure,
                            password change, invite-link redemption, and the
                            originating IP address and user-agent string for each.
                        </li>
                        <li>
                            Platform interaction data — scenarios submitted, run
                            history, artefact downloads, and actions taken inside the
                            operator console.
                        </li>
                    </ul>

                    <h3>3.3 From general Site visitors</h3>
                    <ul>
                        <li>
                            IP address (in transit; not retained beyond short-term
                            operational logs), browser type, user-agent string, and
                            the page path requested.
                        </li>
                        <li>
                            We do not place advertising trackers on the Site. We do
                            not use third-party analytics such as Google Analytics on
                            the public marketing pages.
                        </li>
                    </ul>

                    <h2>4. Lawful bases for processing</h2>
                    <p>
                        We rely on the following lawful bases under Article 6 of the
                        UK GDPR, depending on the processing activity:
                    </p>
                    <ul>
                        <li>
                            <strong>Performance of a contract (Article 6(1)(b))</strong> —
                            account provisioning, user authentication, run execution,
                            artefact storage, and support.
                        </li>
                        <li>
                            <strong>Legitimate interests (Article 6(1)(f))</strong> —
                            security monitoring, fraud prevention, audit logging,
                            service improvement, and internal analytics on platform
                            usage. Our legitimate interests are balanced against the
                            rights and freedoms of data subjects; we have conducted
                            legitimate interests assessments for each such activity.
                        </li>
                        <li>
                            <strong>Consent (Article 6(1)(a))</strong> — optional
                            participation in the training-data mirror described in §5,
                            and any other non-essential processing offered to
                            Authorised Users. Consent can be withdrawn at any time by
                            writing to us at the registered office address in §1;
                            withdrawal does not affect the lawfulness of processing
                            carried out before the withdrawal.
                        </li>
                        <li>
                            <strong>Legal obligation (Article 6(1)(c))</strong> —
                            export-control screening, compliance record-keeping,
                            retention of audit logs and financial records for the
                            periods required by UK law.
                        </li>
                    </ul>

                    <h2>5. Training-data mirror — explicit disclosure</h2>
                    <p>
                        Where Customer has opted in at access-request time, we
                        duplicate a defined, narrow set of run artefacts into an
                        internal training mirror used to improve future releases of
                        the Platform. The artefact set is limited to:
                    </p>
                    <ul>
                        <li>
                            <code>scenario.json</code> — the compiled scenario
                            configuration that was executed.
                        </li>
                        <li>
                            <code>run_results.csv</code> — per-sample engagement
                            outcomes (miss distance, time to intercept, failure mode).
                        </li>
                        <li>
                            <code>trajectories.csv</code> — time-series state samples
                            captured during the run.
                        </li>
                        <li>
                            <code>aggregate.json</code> — summary statistics for the
                            run as a whole.
                        </li>
                    </ul>
                    <p>
                        Each mirror entry is labelled with a one-way SHA-256 hash of
                        the originating tenant identifier (the "tenant_hash"),
                        truncated to sixteen hexadecimal characters. Direct personal
                        identifiers — usernames, real names, email addresses, IP
                        addresses, and any other field that could identify an
                        individual — are stripped before data enters the mirror. The
                        mirror is not shared with any third party, is not sold, and
                        is not used for advertising.
                    </p>
                    <p>
                        You may withdraw from training-mirror participation at any
                        time by writing to us at the registered office address in §1.
                        Withdrawal applies to future runs only; mirror entries
                        created while consent was in force can be deleted on request
                        by the tenant_hash associated with Customer's tenant.
                        Deletion is irreversible. Retention of mirror entries, while
                        consent is active, is indefinite.
                    </p>

                    <h2>6. How we use personal data</h2>
                    <ul>
                        <li>
                            Provisioning and operating Customer tenants; authenticating
                            Authorised Users; executing runs; producing and storing
                            artefacts.
                        </li>
                        <li>
                            Providing support in response to Customer requests, and
                            proactively where an incident requires contact.
                        </li>
                        <li>
                            Security monitoring, detection of abuse and unauthorised
                            access, incident response, and post-incident forensics.
                        </li>
                        <li>
                            Invoicing, billing, and maintenance of the commercial
                            records required by UK tax and company law.
                        </li>
                        <li>
                            Compliance verification — including sanctions screening
                            and export-control review — at access-request time and
                            during the Subscription Term.
                        </li>
                        <li>
                            Sales and relationship management in response to inbound
                            access requests and during active customer relationships.
                        </li>
                        <li>
                            Internal analytics on aggregate platform usage, to inform
                            product priorities and capacity planning.
                        </li>
                    </ul>

                    <h2>7. Third-party recipients (sub-processors)</h2>
                    <p>
                        We use a small number of sub-processors to deliver the
                        Services. Each is bound by a written data-processing agreement
                        and is subject to security and confidentiality obligations
                        consistent with UK GDPR. Our current sub-processors are:
                    </p>
                    <ul>
                        <li>
                            <strong>Capsule CRM (Zestia Ltd)</strong> — customer
                            relationship management system used to manage inbound
                            access requests and active customer accounts. Categories
                            of data processed: name, work email, organisation name,
                            country, role or job title, use-case description. Purpose:
                            inbound enquiry triage, sales pipeline management, account
                            notes. Processing location: United Kingdom / European
                            Union.
                        </li>
                        <li>
                            <strong>Hosting and platform infrastructure</strong> — the
                            Platform is hosted on cloud infrastructure operated by
                            emergent.sh and the underlying cloud provider on which
                            the emergent.sh service runs. Categories of data
                            processed: all data at rest and in transit, including
                            Customer Data. Purpose: operational hosting and delivery
                            of the Services.
                        </li>
                    </ul>
                    <p>
                        We do not use advertising networks, third-party analytics
                        services, or social-media trackers on the Site or inside the
                        operator console. If this changes in future we will update
                        this notice and notify active customers by email.
                    </p>

                    <h2>8. International transfers</h2>
                    <p>
                        Where personal data is transferred outside the United Kingdom
                        we apply the transfer mechanism appropriate to the
                        destination: UK-to-EEA transfers are treated as adequate
                        under the UK Government's adequacy determination for the EEA;
                        any UK-to-US transfers are covered by the UK Addendum to the
                        EU Standard Contractual Clauses, the International Data
                        Transfer Agreement (IDTA), or the EU–US / UK–US Data Privacy
                        Framework, as applicable to the recipient; and transfers to
                        other jurisdictions are covered by the IDTA or equivalent
                        safeguards.
                    </p>

                    <h2>9. Retention</h2>
                    <ul>
                        <li>
                            <strong>Active account data</strong> — retained for the
                            Subscription Term and for seven (7) years after
                            termination, to comply with commercial and tax
                            record-keeping obligations.
                        </li>
                        <li>
                            <strong>Authentication logs</strong> — retained for
                            twelve (12) months.
                        </li>
                        <li>
                            <strong>Run artefacts</strong> — retained for the
                            Subscription Term plus seven (7) years, unless the
                            Customer requests earlier deletion.
                        </li>
                        <li>
                            <strong>Training-mirror entries (anonymised)</strong> —
                            retained indefinitely while consent is active, and
                            deleted on request as described in §5.
                        </li>
                        <li>
                            <strong>Access-request submissions that are declined</strong>
                            {" "}— retained for twelve (12) months to support
                            sanctions-screening audit requirements, then deleted.
                        </li>
                    </ul>

                    <h2>10. Your rights under UK GDPR</h2>
                    <p>
                        Subject to the conditions set out in UK GDPR, you have the
                        right to:
                    </p>
                    <ul>
                        <li>
                            <strong>Access</strong> the personal data we hold about
                            you.
                        </li>
                        <li>
                            <strong>Rectification</strong> of inaccurate or incomplete
                            data.
                        </li>
                        <li>
                            <strong>Erasure</strong> of your personal data ("right to
                            be forgotten"), where the conditions in Article 17 are
                            met.
                        </li>
                        <li>
                            <strong>Restriction</strong> of processing in the
                            circumstances described in Article 18.
                        </li>
                        <li>
                            <strong>Data portability</strong> — to receive your data
                            in a structured, commonly used, machine-readable format,
                            where processing is based on consent or on contract and
                            is carried out by automated means.
                        </li>
                        <li>
                            <strong>Object</strong> to processing carried out under
                            legitimate interests, including profiling.
                        </li>
                        <li>
                            <strong>Withdraw consent</strong> at any time, where
                            processing is based on consent.
                        </li>
                        <li>
                            <strong>Lodge a complaint</strong> with the UK
                            Information Commissioner's Office — see §16.
                        </li>
                    </ul>
                    <p>
                        To exercise any of these rights, write to The RLK Group in
                        writing at its registered office: Unit 4 Strathclyde
                        Business Centre, Cambuslang, United Kingdom, G72 7XR. We
                        will respond within one month of receiving a verified
                        request; that period may be extended by up to two further
                        months where the request is complex or numerous, in which
                        case we will tell you and explain why.
                    </p>

                    <h2>11. Children's data</h2>
                    <p>
                        The Platform is intended for professional use by defence,
                        government, law-enforcement, licensed-industry, and academic
                        research teams. It is not directed at persons under eighteen
                        (18). We do not knowingly collect personal data from
                        children. If you believe we have inadvertently collected such
                        data, write to us at the registered office address in §1 and
                        we will delete it promptly.
                    </p>

                    <h2>12. Cookies and tracking</h2>
                    <p>
                        The public Site uses only strictly necessary cookies — for
                        session continuity inside the operator console and for
                        remembering UI preferences. We do not use advertising
                        cookies, social-media tracking pixels, or cross-site
                        third-party analytics on the Site. Where a cookie exists
                        purely for security or session management, we rely on the
                        "strictly necessary" exemption in the UK PECR; we will
                        display a cookie notice if we ever add a non-essential
                        cookie.
                    </p>

                    <h2>13. Security</h2>
                    <p>
                        We apply technical and organisational measures proportionate
                        to the nature of the data we process and to the counter-UAS
                        customer profile we serve. Our security posture is
                        summarised on the public <Link to="/security">Security</Link>{" "}
                        page and is covered in more detail under NDA during
                        procurement evaluation. Key controls include encryption of
                        data in transit and at rest, strict per-customer tenant
                        isolation, role-based access controls, and an auditable
                        access trail for all authentication and run-lifecycle events.
                    </p>

                    <h2>14. Data breaches</h2>
                    <p>
                        If we become aware of a personal data breach that is likely
                        to result in a risk to the rights and freedoms of individuals,
                        we will notify the ICO without undue delay and, where
                        feasible, within seventy-two (72) hours of becoming aware of
                        it, in line with UK GDPR Article 33. Where the breach is
                        likely to result in a high risk to rights and freedoms, we
                        will also communicate the breach directly to affected
                        individuals in line with Article 34.
                    </p>

                    <h2>15. Changes to this notice</h2>
                    <p>
                        We may update this notice from time to time. The current
                        version is always available at{" "}
                        <Link to="/privacy">/privacy</Link>, with the effective date
                        shown at the top. Where a change is material, we will notify
                        active customers by email at least thirty (30) days before
                        the change takes effect.
                    </p>

                    <h2>16. Contact and complaints</h2>
                    <p>
                        For privacy enquiries or to exercise your rights, write to
                        The RLK Group in writing at its registered office: Unit 4
                        Strathclyde Business Centre, Cambuslang, United Kingdom,
                        G72 7XR. You also have the right to lodge a complaint about
                        our handling of your personal data with the UK Information
                        Commissioner's Office:
                    </p>
                    <p>
                        <strong>Information Commissioner's Office</strong>
                        <br />
                        Wycliffe House, Water Lane, Wilmslow, Cheshire, SK9 5AF,
                        United Kingdom
                        <br />
                        Website:{" "}
                        <a
                            href="https://ico.org.uk"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            https://ico.org.uk
                        </a>
                    </p>
                    <p>
                        We would appreciate the opportunity to resolve any concern
                        before you approach the ICO; please give us a reasonable
                        opportunity to respond by writing to our registered office
                        first.
                    </p>

                    <div
                        className="legal-doc__crosslinks"
                        data-testid="privacy-crosslinks"
                    >
                        <span>See also:</span>
                        <Link to="/terms">Terms of Service</Link>
                        <span>·</span>
                        <Link to="/aup">Acceptable Use Policy</Link>
                    </div>
                    <div className="legal-doc__updated">Last updated: {EFFECTIVE_DATE}</div>
                </div>
            </section>
        </div>
        </>
    );
}
